import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useTranslations } from 'hooks/useTranslations';
import { useParams } from 'react-router-dom';
import { useAlert } from 'models/alertContext';
import { Grid } from '@mui/material';
import { Elabels } from '../../helpers';
import { Accordion } from 'components/molecules/Accordion';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { Resolver, useForm } from 'react-hook-form';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import { useCase } from '../../hooks';
import { Loader } from 'components/atoms/Loader';
import { FormInput } from 'components/molecules/FormInput';
import { Box } from '@mui/system';
import { Alert } from 'components/atoms/Alert';
import { countEditorCharacters } from 'components/organisms/HtmlSlate';
import { yupResolver } from '@hookform/resolvers/yup';
import { ICommentFormValues } from 'models/cases';
import { CommentsAccordions } from '../CommentsAccordions';
import { ThreadForm } from './ThreadForm';
const initialFormValues = { comment: '', topic: '' };

interface IDetailsComments {
  canManageComments: boolean;
}

const DetailsComments = ({ canManageComments }: IDetailsComments) => {
  const { fileId = '' } = useParams<{ fileId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const {
    getApplicationCommentsData,
    applicationCommentsData,
    addApplicationComment,
    editApplicationThread,
  } = useCase();
  const { t } = useTranslations();
  const [isCommentForm, setIsCommentForm] = useState(false);
  const [replyThreadId, setReplyThreadId] = useState<string | null>(null);
  const [editCommentId, setEditCommentId] = useState<string | null>(null);
  const [editThreadId, setEditThreadId] = useState<string | null>(null);
  const { samePageAlert } = useAlert();

  const schema = yup.object({
    topic: yup
      .string()
      .required(t('general.field.validation.required'))
      .max(250, t('general.field.validation.maxCharacters').replace('{0}', '250'))
      .trim(),
    comment: yup
      .string()
      .required(t('general.field.validation.required'))
      .test(
        'editor-validation',
        t('general.field.validation.maxCharacters').replace('{0}', '1000'),
        (value) => countEditorCharacters(value) <= 1000,
      )
      .trim(),
  });

  useEffect(() => {
    (async function init() {
      setIsLoading(true);
      await getApplicationCommentsData(fileId);
      setIsLoading(false);
    })();
  }, []);

  const reloadApplicationCommentsData = async () => {
    await getApplicationCommentsData(fileId);
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<ICommentFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<ICommentFormValues>,
  });

  const closeCommentForm = () => {
    setIsCommentForm(false);
    reset(initialFormValues);
  };

  const submitForm = async (data: any) => {
    const res = await addApplicationComment(fileId, data);
    if (res) {
      closeCommentForm();
      reset(initialFormValues);
      reloadApplicationCommentsData();
    }
  };

  const submitThreadEditForm = async (data: any) => {
    if (editThreadId) {
      const res = await editApplicationThread(fileId, editThreadId, data);
      if (res) {
        setEditThreadId(null);
        reloadApplicationCommentsData();
      }
    }
  };

  return (
    <>
      {isLoading && <Loader isLoading />}
      {!isLoading && (
        <Grid style={{ display: 'grid', alignContent: 'start' }} sx={{ gap: '20px' }}>
          <Accordion
            title={t(Elabels.comments)}
            name="comments"
            alwaysExpanded
            expanded
            buttons={
              canManageComments ? (
                <Button
                  variant={EButtonVariants.outlined}
                  label={t(Elabels.addComment)}
                  size={EButtonSizes.small}
                  disabled={
                    isCommentForm || !!replyThreadId || !!editCommentId || !!editThreadId
                  }
                  onClick={(e) => {
                    setIsCommentForm(true);
                  }}
                  id="details_comments_add-button"
                />
              ) : (
                ''
              )
            }
          >
            <CommentsAccordions
              items={applicationCommentsData}
              maxVisibleItems={12}
              reloadApplicationCommentsData={reloadApplicationCommentsData}
              replyThreadId={replyThreadId}
              setReplyThreadId={setReplyThreadId}
              isCommentForm={isCommentForm}
              canManageComments={canManageComments}
              editCommentId={editCommentId}
              setEditCommentId={setEditCommentId}
              editThreadId={editThreadId}
              setEditThreadId={setEditThreadId}
            />
            <Grid sx={{ mt: 2 }}>
              {samePageAlert && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
                </Box>
              )}
              {editThreadId && (
                <ThreadForm
                  submitForm={submitThreadEditForm}
                  closeTreadForm={() => setEditThreadId(null)}
                  editFormValues={{
                    topic:
                      applicationCommentsData.find((data) => data.uniqueId === editThreadId)
                        ?.topic ?? '',
                  }}
                />
              )}
              {isCommentForm && !replyThreadId && !editCommentId && !editThreadId && (
                <form onSubmit={handleSubmit(submitForm)}>
                  <Grid container>
                    <Grid size={{ xs: 12 }}>
                      <FormInput
                        name="topic"
                        label={t(Elabels.topic)}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        charsLimit={255}
                        required
                        id="details_comments_topic-input"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormWyswigEditor
                        name="comment"
                        label={t(Elabels.comment)}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        setError={setError}
                        clearErrors={clearErrors}
                        charsLimit={1000}
                        required
                        fullContainer
                        showCounter
                      />
                    </Grid>
                    <Grid
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', xl: 'row' },
                        gap: '12px',
                        mt: '20px',
                        width: '100%',
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Button
                          fullWidth
                          label={t('general.cancel.button')}
                          variant={EButtonVariants.outlined}
                          onClick={closeCommentForm}
                          size={EButtonSizes.small}
                          id="details_comments_cancel-button"
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Button
                          variant={EButtonVariants.contained}
                          label={t('general.save.button')}
                          type="submit"
                          fullWidth
                          size={EButtonSizes.small}
                          id="details_comments_save-button"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Grid>
          </Accordion>
        </Grid>
      )}
    </>
  );
};

export default DetailsComments;
